import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as Sentry from "@sentry/react";
import { Checkbox } from "@/components/ui/checkbox";
import { Package, Camera, Loader2, Link2 } from "lucide-react";

interface ConfigLinkerProps {
  briefConfigId: string;
}

interface LinkableConfig {
  id: string;
  name: string;
  type: string;
  linked: boolean;
}

export function ConfigLinker({ briefConfigId }: ConfigLinkerProps) {
  const { user } = useAuth();
  const [productConfigs, setProductConfigs] = useState<LinkableConfig[]>([]);
  const [photographerConfigs, setPhotographerConfigs] = useState<LinkableConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user || !briefConfigId) return;
    setLoading(true);
    setError(null);

    const [configsRes, linksRes] = await Promise.all([
      supabase
        .from("wizard_configs")
        .select("id, name, type")
        .in("type", ["product", "photographer"]),
      supabase
        .from("config_links")
        .select("linked_config_id")
        .eq("brief_config_id", briefConfigId),
    ]);

    if (configsRes.error || linksRes.error) {
      const err = configsRes.error || linksRes.error;
      console.error("Failed to load config links:", err);
      Sentry.captureException(err);
      setError("Failed to load linked configs.");
      setLoading(false);
      return;
    }

    const linkedIds = new Set((linksRes.data || []).map((l: any) => l.linked_config_id));

    const products: LinkableConfig[] = [];
    const photographers: LinkableConfig[] = [];

    for (const c of configsRes.data || []) {
      const item = { id: c.id, name: c.name, type: c.type, linked: linkedIds.has(c.id) };
      if (c.type === "product") products.push(item);
      else photographers.push(item);
    }

    setProductConfigs(products);
    setPhotographerConfigs(photographers);
    setLoading(false);
  }, [user, briefConfigId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (linkedConfigId: string, linkedType: string, currentlyLinked: boolean) => {
    if (!user) return;

    const { error } = currentlyLinked
      ? await supabase
          .from("config_links")
          .delete()
          .eq("brief_config_id", briefConfigId)
          .eq("linked_config_id", linkedConfigId)
      : await supabase.from("config_links").insert({
          brief_config_id: briefConfigId,
          linked_config_id: linkedConfigId,
          linked_type: linkedType,
          user_id: user.id,
        } as any);

    if (error) {
      console.error("Failed to toggle config link:", error);
      Sentry.captureException(error);
      return;
    }

    const setter = linkedType === "product" ? setProductConfigs : setPhotographerConfigs;
    setter(prev => prev.map(c => c.id === linkedConfigId ? { ...c, linked: !currentlyLinked } : c));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-destructive p-2">{error}</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
        <Link2 className="w-3 h-3" />
        Linked Configs
      </div>
      <p className="text-xs text-muted-foreground">
        Link product forms and photographer styles to this brief. When linked, only these will be available during brief creation.
        If none are linked, all configs remain available.
      </p>

      {/* Product Forms */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Package className="w-3 h-3" />
          Product Forms
        </div>
        {productConfigs.length === 0 ? (
          <p className="text-xs text-muted-foreground/60 pl-4">No product forms created yet</p>
        ) : (
          <div className="space-y-1 pl-1">
            {productConfigs.map(c => (
              <div key={c.id} className="flex items-center gap-2 py-1 px-2 rounded-md hover:bg-muted/50 transition-colors">
                <Checkbox
                  id={`product-${c.id}`}
                  checked={c.linked}
                  onCheckedChange={() => toggle(c.id, "product", c.linked)}
                />
                <label htmlFor={`product-${c.id}`} className="text-sm cursor-pointer">{c.name}</label>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photographer Styles */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Camera className="w-3 h-3" />
          Photographer Styles
        </div>
        {photographerConfigs.length === 0 ? (
          <p className="text-xs text-muted-foreground/60 pl-4">No photographer styles created yet</p>
        ) : (
          <div className="space-y-1 pl-1">
            {photographerConfigs.map(c => (
              <div key={c.id} className="flex items-center gap-2 py-1 px-2 rounded-md hover:bg-muted/50 transition-colors">
                <Checkbox
                  id={`photographer-${c.id}`}
                  checked={c.linked}
                  onCheckedChange={() => toggle(c.id, "photographer", c.linked)}
                />
                <label htmlFor={`photographer-${c.id}`} className="text-sm cursor-pointer">{c.name}</label>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
